import { NumberParam, StringParam, withDefault } from 'use-query-params';
import { useNoddiQueryParam } from '../../../../hooks/useNoddiQueryParam';
import { NoddiPaginationState } from '../../../../types/pagination';
import { DEFAULT_PAGE_SIZE } from './paginationDefaults';

const defaultPaginationQueryParamKeys = {
  page: 'page',
  pageSize: 'page-size',
  search: 'search'
} as const;

function getPaginationQueryParamKeys(prefix?: string) {
  if (!prefix) {
    return defaultPaginationQueryParamKeys;
  }
  return {
    page: `${prefix}-page`,
    pageSize: `${prefix}-page-size`,
    search: `${prefix}-search`
  } as const;
}

interface Params {
  initialPage?: number;
  initialPageSize?: number;
  initialSearch?: string;
  /** Optional prefix for URL param keys so multiple tables on the same page (e.g. tabs) keep separate pagination/search state. */
  paramKeyPrefix?: string;
}

export function usePaginationQueryParam(params?: Params) {
  const { initialPage = 0, initialPageSize = DEFAULT_PAGE_SIZE, initialSearch = '', paramKeyPrefix } = params ?? {};

  const paginationQueryParamKeys = getPaginationQueryParamKeys(paramKeyPrefix);

  const [pageIndex, setPageIndex] = useNoddiQueryParam(
    paginationQueryParamKeys.page,
    withDefault(NumberParam, initialPage)
  );
  const [pageSize, setPageSize] = useNoddiQueryParam(
    paginationQueryParamKeys.pageSize,
    withDefault(NumberParam, initialPageSize)
  );
  const [search, setSearch] = useNoddiQueryParam(
    paginationQueryParamKeys.search,
    withDefault(StringParam, initialSearch)
  );

  // Custom pageSize setter that also resets page to initial value
  const handleSetPageSize = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPageIndex(initialPage);
  };

  // Custom search setter that also resets page to initial value
  const handleSetSearch = (newSearch: string) => {
    setSearch(newSearch);
    setPageIndex(initialPage);
  };

  const setPagination = (pagination: NoddiPaginationState) => {
    setPageIndex(pagination.pageIndex);
    setPageSize(pagination.pageSize);
    setSearch(pagination.search);
  };

  return {
    pageIndex,
    setPageIndex,
    pageSize,
    setPageSize: handleSetPageSize,
    search,
    setSearch: handleSetSearch,
    paginationQueryParamKeys,
    pagination: { pageIndex, pageSize, search },
    setPagination
  };
}
